import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api, ApiError } from '../lib/api'
import { outbox } from '../lib/outbox'
import type { PhotoRef } from '../lib/photos'
import { PhotoButton } from '../ui/PhotoButton'
import { useApi, useConnection, useGeolocation } from '../lib/hooks'
import { maskedPhone, money } from '../lib/format'
import {
  Banner, Empty, Evidence, IconCheck, IconWifiOff, Loading, Panel, Pill, Stepper,
} from '../ui/kit'
import { FieldHeader } from './FieldShell'
import type { TodayRoute } from './Today'

interface Grade {
  id: string; code: string; name: string; rank: number; specText: string
  sizeMinMm: number | null; sizeMaxMm: number | null; isSellable: boolean
}
interface Crop { id: string; code: string; name: string; localName: string | null; grades: Grade[] }
interface Packaging { id: string; code: string; name: string; tareKg: number; nominalKg: number | null }
interface Scale { id: string; code: string; certNumber: string | null; resolutionKg: number; driftFlag: boolean }
interface Bundle { crops: Crop[]; packaging: Packaging[]; scales: Scale[] }
interface PriceLine { cropId: string; gradeId: string; buyPricePerKg: number }
interface Board { id: string; code: string; lines: PriceLine[] }

type Step = 1 | 2 | 3 | 4

export function WeighPay() {
  const { stopId } = useParams()
  const nav = useNavigate()
  const { online } = useConnection()
  const { coords } = useGeolocation()

  const route = useApi<TodayRoute>('/routes/today', { cacheKey: 'route.today' })
  const bundle = useApi<Bundle>('/reference/bundle', { cacheKey: 'reference.bundle' })
  const board = useApi<Board>('/pricing/current', { cacheKey: 'pricing.current' })

  const [step, setStep] = useState<Step>(1)
  const [gradeId, setGradeId] = useState<string>('')
  const [packagingId, setPackagingId] = useState<string>('')
  const [gross, setGross] = useState('')
  const [scaleId, setScaleId] = useState<string>('')
  const [photo, setPhoto] = useState<PhotoRef | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<{ lotCode: string; amount: number; queued: boolean } | null>(null)

  const stop = route.data?.stops.find((s) => s.id === stopId)
  const crop = useMemo(
    () => bundle.data?.crops.find((c) => c.id === stop?.crop?.id) ?? bundle.data?.crops[0],
    [bundle.data, stop],
  )
  const grades = crop?.grades ?? []
  const grade = grades.find((g) => g.id === gradeId) ?? grades[0]
  const packaging = bundle.data?.packaging.find((p) => p.id === packagingId)
  const scale = bundle.data?.scales.find((s) => s.id === scaleId) ?? bundle.data?.scales[0]

  const tare = packaging ? Number(packaging.tareKg) : 0
  const grossKg = Number(gross) || 0
  const netKg = Math.max(0, grossKg - tare)

  const price = useMemo(() => {
    if (!crop || !grade || !board.data) return null
    const line = board.data.lines.find((l) => l.cropId === crop.id && l.gradeId === grade.id)
    return line ? Number(line.buyPricePerKg) : null
  }, [crop, grade, board.data])

  const payable = price !== null ? Math.round(netKg * price * 100) / 100 : 0

  if (route.loading || bundle.loading) {
    return (
      <>
        <FieldHeader back={() => nav('/m')} title="Weigh & pay" />
        <div className="field-body"><Panel><Loading rows={4} /></Panel></div>
      </>
    )
  }

  if (!stop) {
    return (
      <>
        <FieldHeader back={() => nav('/m')} title="Weigh & pay" />
        <div className="field-body">
          <Empty title="That stop is not on today's route">
            Go back to Today and pick a farmer from the list.
          </Empty>
        </div>
      </>
    )
  }

  if (stop.lot && !done) {
    return (
      <>
        <FieldHeader back={() => nav('/m')} title="Weigh & pay" />
        <div className="field-body">
          <Banner tone="info" title={`${stop.farmer.name} is already collected`}>
            Lot {stop.lot.code} · {Number(stop.lot.farmgateKg).toFixed(1)} kg ·{' '}
            {money(stop.lot.value)}. Re-weighing would create a second purchase, so the capture is
            closed.
          </Banner>
          <button className="btn block" onClick={() => nav(`/m/lot/${stop.lot!.code}`)}>
            Open the lot trace
          </button>
        </div>
      </>
    )
  }

  if (done) {
    return (
      <>
        <FieldHeader back={() => nav('/m')} title="Done" />
        <div className="field-body">
          <div className="brand-card" style={{ alignItems: 'center', textAlign: 'center' }}>
            <span className="micro">{done.queued ? 'Queued on this device' : 'Recorded'}</span>
            <div className="bc-hero num">{money(done.amount)}</div>
            <p style={{ color: 'var(--on-brand-dim)', fontSize: 'var(--fs-sm)' }}>
              {stop.farmer.name} · lot {done.lotCode}
            </p>
            {done.queued && (
              <p style={{ color: 'var(--on-brand-dim)', fontSize: 'var(--fs-xs)' }}>
                It will send itself the moment you have a signal. Nothing is lost and nothing will be
                paid twice.
              </p>
            )}
          </div>
          <button className="btn lg block" onClick={() => nav('/m')}>Next farmer</button>
          <button className="btn ghost block" onClick={() => nav('/m/queue')}>See the queue</button>
        </div>
      </>
    )
  }

  async function submit() {
    setBusy(true)
    setError(null)

    const clientUuid = crypto.randomUUID()
    const capturePayload = {
      clientUuid,
      farmerId: stop!.farmer.id,
      cropId: crop!.id,
      gradeId: grade!.id,
      grossKg,
      tareKg: tare,
      routeId: route.data!.id,
      routeStopId: stop!.id,
      packagingId: packaging?.id ?? null,
      scaleId: scale?.id ?? null,
      method: 'scale',
      lat: coords?.lat ?? null,
      lng: coords?.lng ?? null,
      gpsAccuracyM: coords?.accuracy ?? null,
      photoUrl: photo,
      deviceId: localStorage.getItem('ledger.device'),
      capturedAt: new Date().toISOString(),
    }

    // The idempotency key is generated here, before anything is sent, so an offline
    // replay and an online retry collapse onto the same payment.
    const paymentUuid = crypto.randomUUID()
    const idempotencyKey = `${clientUuid}:pay`

    if (!online) {
      await outbox.add({
        clientUuid,
        opType: 'collection.capture',
        payload: capturePayload,
        label: stop!.farmer.name,
        sublabel: `${crop!.name} · ${netKg.toFixed(1)} kg`,
        weightKg: netKg,
      })
      await outbox.add({
        clientUuid: paymentUuid,
        opType: 'payment.request',
        payload: {
          clientUuid: paymentUuid,
          lotClientUuid: clientUuid,
          idempotencyKey,
          amount: payable,
          deviceId: localStorage.getItem('ledger.device'),
          lat: coords?.lat ?? null,
          lng: coords?.lng ?? null,
        },
        label: `Pay ${stop!.farmer.name}`,
        sublabel: maskedPhone(stop!.farmer.msisdn),
        amount: payable,
      })
      setBusy(false)
      setDone({ lotCode: 'pending', amount: payable, queued: true })
      return
    }

    try {
      const lot = await api.post<{ id: string; code: string; payable: number }>(
        '/collections/capture',
        capturePayload,
      )
      await api.post('/payments/request', {
        clientUuid: paymentUuid,
        lotId: lot.id,
        idempotencyKey,
        amount: Number(lot.payable),
        deviceId: localStorage.getItem('ledger.device'),
        lat: coords?.lat ?? null,
        lng: coords?.lng ?? null,
      })
      setDone({ lotCode: lot.code, amount: Number(lot.payable), queued: false })
      route.reload()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not record this collection.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <FieldHeader
        back={() => (step === 1 ? nav('/m') : setStep((s) => (s - 1) as Step))}
        title="Weigh & pay"
        right={<Stepper total={4} current={step} />}
      />
      <div className="field-sub">
        <span className="micro">
          Step {step} of 4 · {['Farmer', 'Weigh', 'Price', 'Pay'][step - 1]}
        </span>
      </div>

      <div className="field-body">
        {!online && (
          <Banner tone="warn" icon={<IconWifiOff size={16} />} title="Offline">
            Carry on. This collection and its payment will queue on the device.
          </Banner>
        )}

        {/* ---------------------------------------------------- 1. farmer */}
        {step === 1 && (
          <>
            <Panel title="Farmer" meta={stop.farmer.kycStatus === 'verified'
              ? <Pill tone="ok">ID verified</Pill>
              : <Pill tone="warn">KYC {stop.farmer.kycStatus}</Pill>}>
              <div className="panel-body col" style={{ gap: 12 }}>
                <div className="row" style={{ gap: 12 }}>
                  <span className="avatar" style={{ width: 44, height: 44, fontSize: 'var(--fs-md)',
                    background: 'var(--brand-100)', color: 'var(--brand-700)', border: '1px solid var(--brand-line)' }}>
                    {stop.farmer.name.split(' ').slice(0, 2).map((p) => p[0]).join('')}
                  </span>
                  <div className="grow">
                    <b style={{ fontSize: 'var(--fs-lg)' }}>{stop.farmer.name}</b>
                    <div className="faint" style={{ fontSize: 'var(--fs-xs)' }}>
                      {stop.farmer.code} · {maskedPhone(stop.farmer.msisdn)}
                    </div>
                  </div>
                </div>
                <Evidence gps={!!coords} photo={!!photo} />
              </div>
            </Panel>

            {stop.farmer.kycStatus !== 'verified' && (
              <Banner tone="warn" title="This farmer is not verified yet">
                You can still weigh and grade. The payout will be held until someone other than the
                agent who registered them has verified the mobile-money account name.
              </Banner>
            )}

            <Panel title="Crop and grade" meta={crop?.name}>
              <div className="panel-body col" style={{ gap: 10 }}>
                {grades.map((g) => (
                  <button
                    key={g.id}
                    className="stop-row"
                    style={{ borderTop: '1px solid var(--line-2)' }}
                    onClick={() => setGradeId(g.id)}
                  >
                    <span className={`stop-state ${grade?.id === g.id ? 'done' : ''}`}>
                      {grade?.id === g.id ? <IconCheck size={13} /> : g.code}
                    </span>
                    <span className="stop-who">
                      <b>Grade {g.code} — {g.name}</b>
                      <i>{g.specText}</i>
                    </span>
                  </button>
                ))}
              </div>
            </Panel>

            <button className="btn lg block" disabled={!grade} onClick={() => setStep(2)}>
              Continue to weighing
            </button>
          </>
        )}

        {/* ---------------------------------------------------- 2. weigh */}
        {step === 2 && (
          <>
            <div className="readout">
              <div className="spread">
                <span className="micro">
                  {scale ? `Certified scale · ${scale.code}` : 'No scale paired'}
                </span>
                {scale?.driftFlag
                  ? <Pill tone="crit">Drift flagged</Pill>
                  : <Pill tone="ok">Stable</Pill>}
              </div>
              <div className="readout-value">
                {netKg.toFixed(1)} <small>kg</small>
              </div>
              <div className="readout-sub">
                NET · Gross <b>{grossKg.toFixed(1)}</b> − Tare <b>{tare.toFixed(1)}</b>
                {packaging ? ` (${packaging.name})` : ''}
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
                <Evidence photo={!!photo} gps={!!coords} scale={scale?.code ?? null} />
              </div>
            </div>

            {scale?.driftFlag && (
              <Banner tone="crit" title="This scale is flagged for drift">
                Weighments taken on it are held for reconciliation and are not booked to shrink. Use
                another scale if you have one.
              </Banner>
            )}

            <Panel title="Enter the weight">
              <div className="panel-body col" style={{ gap: 12 }}>
                <div className="field">
                  <label htmlFor="gross">Gross weight on the scale (kg)</label>
                  <input
                    id="gross"
                    className="input n"
                    style={{ fontSize: 'var(--fs-2xl)', minHeight: 56, textAlign: 'center' }}
                    type="number"
                    inputMode="decimal"
                    step="0.1"
                    min="0"
                    value={gross}
                    onChange={(e) => setGross(e.target.value)}
                    placeholder="0.0"
                    autoFocus
                  />
                </div>

                <div className="field">
                  <label htmlFor="pack">Container</label>
                  <select id="pack" className="input" value={packagingId} onChange={(e) => setPackagingId(e.target.value)}>
                    <option value="">No container — weighed loose</option>
                    {bundle.data?.packaging.map((p) => (
                      <option key={p.id} value={p.id}>{p.name} · tare {Number(p.tareKg).toFixed(1)} kg</option>
                    ))}
                  </select>
                  <span className="hint">
                    Always buy by weight, never by container. Tare is subtracted automatically.
                  </span>
                </div>

                {bundle.data && bundle.data.scales.length > 1 && (
                  <div className="field">
                    <label htmlFor="scale">Scale</label>
                    <select id="scale" className="input" value={scaleId} onChange={(e) => setScaleId(e.target.value)}>
                      {bundle.data.scales.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.code}{s.certNumber ? ` · cert ${s.certNumber}` : ''}{s.driftFlag ? ' · DRIFT' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <PhotoButton
                  kind="farmgate_scale"
                  where={coords}
                  value={photo}
                  onChange={setPhoto}
                  label="Photograph the scale"
                />
              </div>
            </Panel>

            <button className="btn lg block" disabled={netKg <= 0} onClick={() => setStep(3)}>
              Continue · {netKg.toFixed(1)} kg
            </button>
          </>
        )}

        {/* ---------------------------------------------------- 3. price */}
        {step === 3 && (
          <>
            {price === null ? (
              <Banner tone="crit" title="No published price for this crop and grade">
                A capture without a board price would be a guess, so it is refused. Ask ops to publish
                a price board covering {crop?.name} grade {grade?.code}.
              </Banner>
            ) : (
              <>
                <div className="price-strip">
                  <div className="ps-left">
                    <i>Grade · rate</i>
                    <b>{grade?.code} · {money(price, { currency: false })}/kg</b>
                  </div>
                  <div className="ps-right">
                    <i>Payable — net {netKg.toFixed(1)} kg</i>
                    <b>{money(payable)}</b>
                  </div>
                </div>

                <Panel title="What the farmer is being paid for">
                  <div className="panel-body col" style={{ gap: 8 }}>
                    <Line label="Gross on the scale" value={`${grossKg.toFixed(1)} kg`} />
                    <Line label={`Tare${packaging ? ` · ${packaging.name}` : ''}`} value={`− ${tare.toFixed(1)} kg`} />
                    <div className="divider" />
                    <Line label="Net weight" value={`${netKg.toFixed(1)} kg`} bold />
                    <Line label="Board price" value={`${money(price, { currency: false })} / kg`} />
                    <div className="divider" />
                    <Line label="Payable" value={money(payable)} bold />
                  </div>
                </Panel>

                <p className="hint" style={{ textAlign: 'center' }}>
                  Rate locked from board {board.data?.code} at capture. Any variance at the depot is
                  coded against this lot, not taken off the farmer.
                </p>

                <button className="btn lg block" onClick={() => setStep(4)}>
                  Continue to payment
                </button>
              </>
            )}
          </>
        )}

        {/* ---------------------------------------------------- 4. pay */}
        {step === 4 && (
          <>
            <Panel title="Send the payment">
              <div className="panel-body col" style={{ gap: 12 }}>
                <div className="payout">
                  <span className="payout-mark">M</span>
                  <div className="payout-who">
                    <b>M-PESA send</b>
                    <i>{maskedPhone(stop.farmer.msisdn)} · {stop.farmer.name}</i>
                  </div>
                  <div className="payout-amt">
                    <b>{money(payable, { currency: false })}</b>
                    <span>name matched ✓</span>
                  </div>
                </div>

                {error && <div className="error-text">{error}</div>}

                <button className="btn lg block" disabled={busy || payable <= 0} onClick={() => void submit()}>
                  {busy ? 'Recording…' : `Send ${money(payable)}`}
                  <small>
                    {online
                      ? 'posts to the finance ledger instantly'
                      : 'queues on this device until you have signal'}
                  </small>
                </button>
              </div>
            </Panel>

            <Banner tone="info" title="This cannot pay twice">
              The payment carries a token generated on this device before it was sent. If you tap
              again, if the queue replays, or if the network retries, the server returns the original
              payment instead of creating a second one.
            </Banner>
          </>
        )}
      </div>
    </>
  )
}

function Line({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="spread" style={{ fontSize: bold ? 'var(--fs-lg)' : 'var(--fs-sm)', fontWeight: bold ? 700 : 400 }}>
      <span className={bold ? '' : 'muted'}>{label}</span>
      <span className="num">{value}</span>
    </div>
  )
}
